import React, { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import Pill from "./Pill";
import { ArrowUpDown, ChevronDown, ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react";
import EmptyState from "./EmptyState";
import { Spinner } from "react-bootstrap";
import DropDownToggle from "./DropDown";
import UploadModal from "./UploadModal";
import { _get, _post } from "./axios";
import { IJob, IJobDataResponse, ILogin, IPagination } from "./interfaces";
import { useSearchParams } from "react-router-dom";

import {
  cacheJobs,
  getAccessToken,
  getCachedJobs,
  isTokenExpired,
  setAccessToken,
} from "./localStorageServices";
import DownloadModal from "./DownloadModal";
import FilterModal from "./FilterModal";
import { useToast } from "@/hooks/useToast";
import Details from "./Details";
import LoadingModal from "./LoadingModal";
import PaginationBtn from "./PaginationBtn";


const QuestionCategorizer = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [files, setFiles] = useState<File | null>();
  const [jobs, setJobs] = useState<IJob[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "All";
  const [openDetailsId, setOpenDetailsId] = useState<string | null>(null);
  const { toast } = useToast();
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [viewType, setViewType] = useState<"Waiting" | "Done">("Done");
  const [filteredJobs, setFilteredJobs] = useState<IJob[]>([]);
   const [pagination, setPagination] = useState<IPagination>({
    total_count: 0,
    skip: 0,
    limit: 10, 
    has_next: false,
    has_previous: false,
  });


  useEffect(() => {
    const filtered =
      statusFilter === "All" || null
        ? jobs
        : jobs.filter((job) => job.status === statusFilter.toLowerCase());
    setFilteredJobs(filtered);
  }, [statusFilter, jobs]);

  const hideModal = () => {
    setShowUploadModal(false);
  };

  const hideDownloadModal = () => {
    setShowDownloadModal(false);
  };

  const openDownloadModal = () => {
    setShowDownloadModal(true);
  };

  const [openDropdownId, setOpenDropdownId] = useState(null);

  const handleDropdownToggle = (nextShow, _, id) => {
    if (nextShow) {
      setOpenDropdownId(id);
    } else {
      if (openDropdownId === id) {
        setOpenDropdownId(null);
      }
    }
  };

  const login = async () => {
    try {
      const response = await _post<ILogin>(`/auth/login`, {
        examsoft_url:
          "https://ei14.examsoft.com/STW-war/ei/questions/s=enflux_internal",
        examsoft_username: "Enflux Admin",
        examsoft_acc_name: "Enflux Admin",
        token: "",
        email: "placeholder.admin@enflux.com",
        examsoft_version: 1,
        school_id: "enflux_internal",
      });
      if (
        response.data?.token_type === "bearer" &&
        response.data?.access_token
      ) {
        setAccessToken(response.data.access_token);
        toast({
          type: "success",
          title: "Authenticated successfully",
          message: "Welcome back",
        });
        getJobs({skip: pagination.skip, limit: pagination.limit});
      } 
    } catch (error: any) {
      toast({
        type: "error",
        title: "Authentication Error",
        message:
          "Something went wrong, please check your internet connection and refresh the page, thank you.",
      });
      return {loggedIn: false};
    }
  };

  const getJobs = async ({skip = 0, limit = 100}: {skip?: number; limit?: number} = {}) => {
    setIsLoadingJobs(true);
    let resolved = false;

    const cachedJobs = getCachedJobs();
    const timeoutId = setTimeout(() => {
      if (!resolved && cachedJobs) {
        setJobs(JSON.parse(cachedJobs));
        setIsLoadingJobs(false);
        setShowLoadingModal(true);
        setViewType("Waiting");
      }
    }, 4000);
    try {
      const response = await _get<IJobDataResponse>(`/categorizer/jobs?skip=${skip}&limit=${limit}`);
      clearTimeout(timeoutId);
      if (response.status == 200) {
        setJobs(response.data.jobs);
        cacheJobs(response.data.jobs);
        setFilteredJobs(response.data.jobs);
        setPagination({
          has_next: response.data.pagination.has_next,
          has_previous: response.data.pagination.has_previous,
          limit: response.data.pagination.limit,
          skip: response.data.pagination.skip,
          total_count: response.data.pagination.total_count
        })
        setIsLoadingJobs(false);
        setShowLoadingModal(false);
      }
    } catch (error: any) {
      setIsLoadingJobs(false);
      if (cachedJobs && !resolved) {
        setJobs(JSON.parse(cachedJobs));
      }
    }
  };

    const goToFirst = () => getJobs({ skip: 0, limit: pagination.limit });

  const goToLast = () => {
    const lastSkip = Math.floor((pagination.total_count - 1) / pagination.limit) * pagination.limit;
    getJobs({ skip: lastSkip, limit: pagination.limit });
  };

    const nextPage = () => {
    if (pagination.has_next) {
      getJobs({skip: pagination.skip + pagination.limit, limit: pagination.limit});
    }
  };

  const prevPage = () => {
    if (pagination.has_previous) {
      getJobs({skip: pagination.skip - pagination.limit, limit: pagination.limit});
    }
  };

    const changePageSize = (newLimit: number) => {
    getJobs({ skip: 0, limit: newLimit }); // reset to first page
  };


const PAGE_SIZES = [5, 10, 15, 20];

  const [open, setOpen] = useState(false);


useEffect(() => {
  const init = async () => {
    const token = getAccessToken();

    if (!token || isTokenExpired(token)) {
      await login();
    } else {
      await getJobs({ skip: pagination.skip, limit: pagination.limit });
    }

    setIsLoading(false);
  };

  init();
}, [pagination.skip, pagination.limit]);

  if (isLoadingJobs || isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100 font-sans">
        <Spinner animation="border" role="status" className="text-blue-500">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="ml-3 text-lg text-gray-700">
          Loading data, please wait...
        </p>
      </div>
    );
  }

  return (
    <>
      {showUploadModal && (
        <UploadModal
          show={showUploadModal}
          onHide={hideModal}
          files={files as File}
          setFiles={setFiles}
          getJobs={getJobs}
        />
      )}
      {showDownloadModal && (
        <DownloadModal
          show={showDownloadModal}
          onHide={hideDownloadModal}
          id={activeId}
        />
      )}
      <LoadingModal
        viewType={viewType}
        show={showLoadingModal}
        onHide={() => setShowLoadingModal(false)}
      />
      <div>
        <div className="action-bar">
          <button className="csv-btn" onClick={() => setShowUploadModal(true)}>
            Upload new csv/excel
          </button>
        </div>

        <div className="top">
          {jobs.length > 0 ? (
            <p>
              Showing <span>{filteredJobs.length}</span> out of{" "}
              <span>{jobs.length}</span>
            </p>
          ) : (
            <p></p>
          )}
          <FilterModal />
        </div>

        <div className="">
          {filteredJobs.length > 0 ? (
            <div>
            <Table className="w-full striped text-left table-auto rounded-2xl table-parent">
              <thead className="">
                <tr className="flex">
                  <th className="">Model</th>
                  <th className="">
                    Status <ArrowUpDown color="#5C5C5C" size={16} />
                  </th>
                  <th className=""></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredJobs.map((job: IJob) => (
                  <>
                    <tr key={job.job_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {job.model}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm status">
                        <Pill status={job.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-center ellipsis">
                          <DropDownToggle
                            openDropdownId={openDropdownId}
                            handleDropdownToggle={handleDropdownToggle}
                            id={job.job_id}
                            openDownloadModal={openDownloadModal}
                            setActiveId={setActiveId}
                            setOpenDetailsId={setOpenDetailsId}
                          />
                        </div>
                      </td>
                    </tr>
                    {openDetailsId === job.job_id && (
                      <Details job={job} setActiveId={setOpenDetailsId} />
                    )}
                  </>
                ))}
              </tbody>
            </Table>
            <div className="bg-gray-100 p-4 rounded-b-2xl h-20 pagination">
              <div className="items-container">
                <div className="btn-actions">
                  <div className="page-size-select">
                    {/* Button */}
                    <button
                      type="button"
                      onClick={() => setOpen((prev) => !prev)}
                      className="page-size-select__button"
                    >
                      {pagination.limit} <span style={{color: "#5C5C5C", marginLeft: "4px", marginRight: "6px"}}> per page</span> {open ? <ChevronUp color="#292929" /> : <ChevronDown color="#292929" />}
                    </button>

                    {/* Dropdown */}
                    {open && (
                      <div className="page-size-select__menu">
                        {PAGE_SIZES.map((size) => (
                          <div
                            key={size}
                            className={`page-size-select__option ${
                              pagination.limit === size ? "page-size-select__option--active" : ""
                            }`}
                            onClick={() => {
                              changePageSize(size);
                              setOpen(false);
                            }}
                          >
                            {size}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <PaginationBtn icon={<ChevronFirst  className={`${pagination.has_previous ? "active-icon-class" : "inactive-icon-class"}`} />} action={goToFirst} />
                  <PaginationBtn icon={<ChevronLeft className={`${pagination.has_previous ? "active-icon-class" : "inactive-icon-class"}`} />} action={prevPage} />
                  <PaginationBtn icon={<ChevronRight className={`${pagination.has_next ? "active-icon-class" : "inactive-icon-class"}`} />} action={nextPage} />
                  <PaginationBtn icon={<ChevronLast className={`${pagination.has_next ? "active-icon-class" : "inactive-icon-class"}`} />} action={goToLast} />
                </div>
              </div>
            </div>
            </div>
          ) : (
            <EmptyState
              subtitle="It looks like you haven't added any question to your system yet."
              title="No Question Found"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default QuestionCategorizer;
